import { ArrowClockwiseIcon, WarningCircleIcon } from '@phosphor-icons/react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface WorkflowFailureBannerProps {
  reason: string | null;
  onRetry: () => void;
}

/**
 * Surfaces the most recent step failure inline. The reason is read from
 * `workflow_state.history[N].failure_reason`. Retry fires the existing
 * `mora:workflowAction` `{ action: 'retry' }` event — wired in the parent.
 *
 * Composed from the existing `Alert` + `Button` primitives (see §9.4).
 * Uses `Alert variant="destructive"` per §21.3 F16 ("inline error = Alert,
 * not Empty"). Copy is sentence-case, outcome-led per DESIGN-VOICE.md.
 */
export function WorkflowFailureBanner({
  reason,
  onRetry,
}: WorkflowFailureBannerProps): JSX.Element {
  return (
    <Alert variant="destructive" className="border-border/40">
      <WarningCircleIcon className="size-4" weight="regular" />
      <AlertTitle>Step didn&apos;t finish</AlertTitle>
      <AlertDescription className="space-y-3">
        {reason ? (
          <p className="text-detail leading-relaxed">{reason}</p>
        ) : (
          <p className="text-detail leading-relaxed">
            This step failed. Try again or adjust the brief.
          </p>
        )}
        <div className="flex justify-end">
          <Button variant="flat" size="sm" onClick={onRetry}>
            <ArrowClockwiseIcon className="size-3.5 mr-1" weight="regular" />
            Retry
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
